import { DatePicker, type DatePickerProps } from '@utilities-one/gpm-ui';

import { FormBase } from '@/library/form/components/FormBase';
import { useFieldContext } from '@/library/form/contexts';
import { FormControlProps } from '@/library/form/types/form.type';
import { dateToIso, isoToDate } from '@/library/lang/date';

export type FormDatePickerProps = FormControlProps & {
  placeholder?: string;
  withTime?: boolean;
  disabled?: boolean;
  displayFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  pickerProps?: Omit<
    DatePickerProps,
    'value' | 'onChange' | 'placeholder' | 'withTime' | 'disabled' | 'minDate' | 'maxDate' | 'dateFormat'
  >;
};

/**
 * Date (or date-time) picker bound to an ISO-string field — what the SDK schemas expect
 * (`startTime: z.string()` etc.). The gpm-ui `DatePicker` speaks native `Date`; conversion
 * happens here at the edge (`YYYY-MM-DD` without time, full ISO with).
 */
export function FormDatePicker({
  placeholder,
  withTime = false,
  disabled = false,
  displayFormat,
  minDate,
  maxDate,
  pickerProps,
  ...props
}: FormDatePickerProps) {
  const field = useFieldContext<string>();

  return (
    <FormBase {...props}>
      <DatePicker
        {...pickerProps}
        value={isoToDate(field.state.value)}
        onChange={(d) => field.handleChange(d ? dateToIso(d, withTime) : '')}
        placeholder={placeholder}
        withTime={withTime}
        disabled={disabled}
        dateFormat={displayFormat}
        minDate={minDate}
        maxDate={maxDate}
        className="w-full"
      />
    </FormBase>
  );
}
